import {
  YOU,
  type MemoryFact,
  type MemorySuggestion,
  type ThingKind,
} from "@/api/memory-focus";
import {
  avatarKindOf,
  thingDisplayName,
  thingGlyph,
} from "@/lib/memory-thing-kinds";
import {
  focusAngles,
  frontPoint,
  greatCircle,
  type Vec3,
} from "./globe-projection";
import {
  DRAWN_MAX,
  SLOT_MAX,
  DENSE_AT,
  hashPoint,
  placeTemps,
} from "./globe-layout";
import type {
  GlobeScene,
  SceneInput,
  SceneNode,
  SceneArc,
} from "./globe-scene-types";
import { memoryRecency } from "./memory-recency";
export {
  DRAWN_MAX,
  SLOT_MAX,
  DENSE_AT,
  SLOT_GAP,
  FRONT_CLEAR,
  globeLayout,
  placeTemps,
} from "./globe-layout";
export type { GlobeLayout, SpotRules } from "./globe-layout";
export type {
  GlobeScene,
  SceneInput,
  SceneNode,
  SceneArc,
} from "./globe-scene-types";
const newKey = (end: { kind: ThingKind; name: string }) =>
  `new:${end.kind}:${end.name.trim().toLowerCase().replace(/\s+/g, " ")}`;
export function buildGlobeScene({
  globe,
  layout,
  focus,
  focusId,
  youGlyph,
  now = Date.now(),
}: SceneInput): GlobeScene {
  const you = globe.things.find(({ thing }) => thing.kind === "user_profile");
  const youId = you?.thing.id ?? null;
  const keyOf = (end: { id: string | null; kind: ThingKind; name: string }) =>
    end.id === null ? newKey(end) : end.id === youId ? YOU : end.id;
  const nodes = new Map<string, SceneNode>();
  const add = (
    end: { id: string | null; kind: ThingKind; name: string },
    extra: Partial<SceneNode>,
  ): SceneNode => {
    const avatar = avatarKindOf(end.kind);
    const name = thingDisplayName(end);
    const node: SceneNode = {
      key: keyOf(end),
      thingId: end.id,
      name,
      kind: end.kind,
      avatar,
      glyph: avatar === "you" ? youGlyph : thingGlyph(name, avatar),
      more: 0,
      rank: DRAWN_MAX + SLOT_MAX,
      pos: null,
      temp: false,
      pending: end.id === null,
      recent: null,
      lit: true,
      suggestionIds: [],
      ...extra,
    };
    if (!nodes.has(node.key)) nodes.set(node.key, node);
    return nodes.get(node.key)!;
  };
  add(
    { id: youId, kind: "user_profile", name: "You" },
    { key: YOU, pending: false, rank: -1, more: you?.more_count ?? 0 },
  );
  globe.things.forEach(({ thing, more_count }, rank) => {
    const pos = thing.id ? layout.positions.get(thing.id) : undefined;
    if (pos)
      add(thing, {
        pos,
        rank,
        more: more_count,
        recent: memoryRecency(thing.created_at, thing.updated_at, now),
      });
  });
  const drawn = [...layout.positions.values()];
  const focusKey = focusId === YOU || focusId === youId ? null : focusId;
  let target = layout.home;
  let focusNode = focusKey ? (nodes.get(focusKey) ?? null) : null;
  if (focusKey && !focusNode && focus?.focus?.id === focusKey) {
    const linked = focus.ring
      .map(({ thing }) => thing.id && layout.positions.get(thing.id))
      .find(Boolean);
    const [pos] = placeTemps(linked || hashPoint(focusKey), 1, { drawn });
    focusNode = add(focus.focus, {
      pos,
      temp: true,
      rank: -0.5,
      recent: memoryRecency(
        focus.focus.created_at,
        focus.focus.updated_at,
        now,
      ),
    });
  }
  const spots: Vec3[] = focusNode?.temp ? [focusNode.pos!] : [];
  if (focusNode?.pos) {
    target = focusAngles(focusNode.pos);
    const lit = new Set([focusNode.key]);
    // The loaded backend ring is authoritative; globe facts never widen it.
    const offstage = (focus?.ring ?? [])
      .filter((slot) => {
        const key = keyOf(slot.thing);
        lit.add(key);
        const node = nodes.get(key);
        if (node)
          Object.assign(node, {
            more: slot.more_count,
            suggestionIds: slot.suggestion_ids,
          });
        return !node;
      })
      .slice(0, SLOT_MAX);
    const placed = placeTemps(focusNode.pos, offstage.length, {
      drawn,
      spots,
      front: frontPoint(target),
    });
    offstage.forEach((slot, index) =>
      add(slot.thing, {
        pos: placed[index]!,
        temp: true,
        rank: DRAWN_MAX + index,
        more: slot.more_count,
        suggestionIds: slot.suggestion_ids,
        recent: memoryRecency(
          slot.thing.created_at,
          slot.thing.updated_at,
          now,
        ),
      }),
    );
    if (focus)
      focusNode.more = focus.bundles.reduce(
        (sum, bundle) => sum + bundle.count,
        0,
      );
    for (const node of nodes.values()) node.lit = lit.has(node.key);
  } else if (!focusKey) {
    placeSuggestionEnds(
      globe.suggestions,
      nodes,
      keyOf,
      add,
      { drawn, spots },
      frontPoint(layout.home),
    );
  }
  const arcs = new Map<string, SceneArc>();
  const link = (
    key: string,
    from: string,
    to: string,
    extra: Pick<SceneArc, "factId" | "suggestionId" | "text" | "recent">,
  ) => {
    const a = nodes.get(from),
      b = nodes.get(to);
    if (!a || !b || from === to || arcs.has(key)) return;
    const focusArc =
      focusNode !== null &&
      a.lit &&
      b.lit &&
      (from === focusNode.key || to === focusNode.key);
    arcs.set(key, {
      key,
      from,
      to,
      ...extra,
      focus: focusArc,
      path: a.pos && b.pos ? greatCircle(a.pos, b.pos) : null,
    });
  };
  const factArc = (fact: MemoryFact) =>
    link(fact.id, keyOf(fact.source), keyOf(fact.target), {
      factId: fact.id,
      suggestionId: null,
      text: fact.claim,
      recent: memoryRecency(fact.created_at, fact.updated_at, now),
    });
  globe.facts.forEach(factArc);
  focus?.ring.forEach((slot) => slot.facts.forEach(factArc));
  const suggestions: MemorySuggestion[] = focusNode
    ? (focus?.suggestions ?? [])
    : focusKey
      ? []
      : globe.suggestions;
  for (const item of suggestions) {
    const [from, to] = item.endpoints;
    if (item.type === "fact" && from && to)
      link(`s:${item.id}`, keyOf(from), keyOf(to), {
        factId: null,
        suggestionId: item.id,
        text: item.claim,
        recent: null,
      });
  }
  const nodeList = [...nodes.values()];
  const litIds = nodeList
    .filter((node) => node.lit && node.thingId && node.key !== focusNode?.key)
    .map((node) => node.thingId!)
    .slice(0, DRAWN_MAX + SLOT_MAX);
  return {
    nodes: nodeList,
    arcs: [...arcs.values()],
    focusKey: focusNode?.key ?? null,
    target,
    litIds,
    dense: drawn.length > DENSE_AT,
  };
}
/** At the root, a suggestion's end that is not drawn takes a temporary spot near its drawn end (You's ends sit near the
 * default view's front); at most SLOT_MAX, newest suggestion first. */
function placeSuggestionEnds(
  suggestions: readonly MemorySuggestion[],
  nodes: Map<string, SceneNode>,
  keyOf: (end: MemorySuggestion["endpoints"][number]) => string,
  add: (
    end: MemorySuggestion["endpoints"][number],
    extra: Partial<SceneNode>,
  ) => SceneNode,
  rules: { drawn: Vec3[]; spots: Vec3[] },
  homeFront: Vec3,
): void {
  let placed = 0;
  for (const item of suggestions) {
    for (const end of item.endpoints) {
      const existing = nodes.get(keyOf(end));
      if (existing) {
        if (existing.pending && !existing.suggestionIds.includes(item.id))
          existing.suggestionIds.push(item.id);
        continue;
      }
      if (placed >= SLOT_MAX) continue;
      const other = item.endpoints.find(
        (candidate) => keyOf(candidate) !== keyOf(end),
      );
      const anchor = other
        ? (nodes.get(keyOf(other))?.pos ??
          (keyOf(other) === YOU ? homeFront : null))
        : null;
      if (!anchor) continue;
      const [pos] = placeTemps(anchor, 1, rules);
      rules.spots.push(pos!);
      add(end, {
        pos,
        temp: true,
        rank: DRAWN_MAX + placed,
        suggestionIds: [item.id],
      });
      placed += 1;
    }
  }
}
